import React from 'react';
import { ActiveStatusPill } from '../../../shared/Pill';
import '../../../shared/ListRowHover.css';

const TH_STYLE = { padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left', whiteSpace: 'nowrap' };
const TD_STYLE = { padding: '6px 8px', fontSize: 14, whiteSpace: 'nowrap' };

// Resolves one cell the same way priceResolver.js prices a Product: the
// Customer's assignment on this Product Price Group, else the nearest
// ancestor's, else none (priced at Retail). Inherited values are shown
// greyed so a gap that's actually covered by a parent reads differently
// from a true gap.
function resolveCell(assignmentsByGroup, group, groupsById) {
  const own = assignmentsByGroup.get(String(group.id));
  if (own) return { name: own.customer_price_group?.name, inheritedFrom: null };
  let parentId = group.parent_id;
  while (parentId != null) {
    const parent = groupsById.get(String(parentId));
    if (!parent) break;
    const inherited = assignmentsByGroup.get(String(parent.id));
    if (inherited) return { name: inherited.customer_price_group?.name, inheritedFrom: parent.name };
    parentId = parent.parent_id;
  }
  return null;
}

// Customers screen's "Price Groups" view — one row per Customer, one column
// per active Product Price Group, each cell that Customer's Customer Price
// Group. Read-only: a row click opens the Customer record, where the
// assignments are edited.
function CustomerPriceGroupMatrix({ rows, productGroups, productGroupFilter, loading, onView }) {
  const groupsById = new Map(productGroups.map((g) => [String(g.id), g]));
  const columns = productGroups
    .filter((g) => g.is_active)
    .filter((g) => !productGroupFilter || String(g.id) === String(productGroupFilter));
  const totalCols = 2 + columns.length;

  return (
    <div style={{ margin: '24px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflowX: 'auto', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
      <table className="list-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16 }}>
        <thead className="list-head" style={{ fontSize: 14 }}>
          {/* Band over the per-group columns — without it the column
              headings (Connectors, Cables…) don't say what they are. */}
          <tr>
            <th rowSpan={2} style={{ ...TH_STYLE, minWidth: 220, verticalAlign: 'bottom' }}>Name</th>
            {columns.length > 0 && (
              <th colSpan={columns.length} style={{ ...TH_STYLE, padding: '10px 8px 6px', textAlign: 'center', color: '#5a6b85', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                Product Price Groups
              </th>
            )}
            <th rowSpan={2} style={{ ...TH_STYLE, textAlign: 'center', verticalAlign: 'bottom' }}>Status</th>
          </tr>
          <tr>
            {columns.map((g) => (
              <th key={g.id} style={TH_STYLE} title={g.parent_id != null ? `Under ${groupsById.get(String(g.parent_id))?.name || 'parent group'}` : undefined}>
                {g.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={totalCols} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Loading...</td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={totalCols} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>No results</td></tr>
          ) : rows.map((item) => {
            const assignmentsByGroup = new Map((item.price_groups || []).map((pg) => [String(pg.product_price_group_id), pg]));
            return (
              <tr key={item.id} className="list-row" onClick={() => onView(item)} style={{ cursor: 'pointer' }} title="Click to view">
                <td className="cell-title" style={{ ...TD_STYLE, fontWeight: 600, whiteSpace: 'normal' }}>{item.name}</td>
                {columns.map((g) => {
                  const cell = resolveCell(assignmentsByGroup, g, groupsById);
                  return (
                    <td key={g.id} data-label={g.name} style={TD_STYLE}>
                      {!cell ? (
                        <span style={{ color: '#9aa5b5' }} title="No Customer Price Group — priced at Retail">—</span>
                      ) : cell.inheritedFrom ? (
                        <span style={{ color: '#8a96a8', fontStyle: 'italic' }} title={`Inherited from ${cell.inheritedFrom}`}>{cell.name}</span>
                      ) : (
                        cell.name
                      )}
                    </td>
                  );
                })}
                <td data-label="Status" style={{ ...TD_STYLE, textAlign: 'center', fontSize: 13 }}>
                  <ActiveStatusPill active={item.is_active} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default CustomerPriceGroupMatrix;
